import React from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import logo from "../assets/images/logo.jpg";

export default function PrivacyPolicy() {
  // Scroll to top on page load
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="Dotti Verify"
              className="w-10 h-10 rounded-lg"
            />
            <span className="text-xl font-bold text-gray-800">
              Dotti Verify
            </span>
          </Link>
          <Link
            to="/"
            className="text-gray-600 hover:text-[#007AFF] transition flex items-center gap-1"
          >
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-blue-100 p-4 rounded-full">
              <Shield className="w-8 h-8 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                Privacy Policy
              </h1>
              <p className="text-gray-500 mt-2">Last Updated: March 2026</p>
            </div>
          </div>

          <p className="text-gray-600 text-lg leading-relaxed">
            At Dotti Verify, we value your privacy and are committed to
            protecting your personal information. This Privacy Policy explains
            how we collect, use, and protect the information you provide when
            using our services.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                1
              </span>
              Information We Collect
            </h2>
            <p className="text-gray-600 mb-4">
              When you use Dotti Verify, we may collect the following
              information:
            </p>
            <ul className="list-disc list-inside space-y-3 text-gray-600 ml-4">
              <li className="pl-2">
                <span className="font-semibold">Personal information</span> such
                as your name, email address, and phone number
              </li>
              <li className="pl-2">
                <span className="font-semibold">Account information</span> used
                to create and manage your profile
              </li>
              <li className="pl-2">
                <span className="font-semibold">Transaction information</span>{" "}
                related to payments and purchases
              </li>
              <li className="pl-2">
                <span className="font-semibold">Technical information</span>{" "}
                such as device type, IP address, and browser information
              </li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                2
              </span>
              How We Use Your Information
            </h2>
            <p className="text-gray-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-3 text-gray-600 ml-4">
              <li className="pl-2">
                Provide and improve our verification services
              </li>
              <li className="pl-2">Process payments and transactions</li>
              <li className="pl-2">Maintain the security of our platform</li>
              <li className="pl-2">
                Communicate with users about updates, support, or service issues
              </li>
              <li className="pl-2">
                Prevent fraud and unauthorized activities
              </li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                3
              </span>
              Payment Processing
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Payments made on Dotti Verify may be processed through third-party
              payment providers such as Squardo or other secure payment
              processors. These providers may collect and process payment
              information according to their own privacy policies.
            </p>
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                4
              </span>
              Data Protection
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We take reasonable security measures to protect your personal
              information from unauthorized access, misuse, or disclosure.
              However, no online system can be guaranteed to be completely
              secure.
            </p>
          </div>

          {/* Section 5 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                5
              </span>
              Sharing of Information
            </h2>
            <p className="text-gray-600 mb-4">
              We do not sell or rent your personal information. Your information
              may only be shared:
            </p>
            <ul className="list-disc list-inside space-y-3 text-gray-600 ml-4">
              <li className="pl-2">
                With trusted service providers necessary to operate the platform
              </li>
              <li className="pl-2">If required by law or legal process</li>
              <li className="pl-2">
                To protect the rights and safety of Dotti Verify and its users
              </li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                6
              </span>
              Cookies and Tracking
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Our website may use cookies or similar technologies to improve
              user experience, analyze traffic, and maintain security.
            </p>
          </div>

          {/* Section 7 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                7
              </span>
              User Rights
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Users may request access to their personal data or request
              correction or deletion of their information, subject to legal and
              operational requirements.
            </p>
          </div>

          {/* Section 8 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-sm">
                8
              </span>
              Changes to This Policy
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Dotti Verify may update this Privacy Policy from time to time.
              Updates will be posted on this page.
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Dotti Verify. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link
              to="/PrivacyPolicy"
              className="text-[#007AFF] hover:underline"
            >
              Privacy Policy
            </Link>
            <span>|</span>
            <Link
              to="/TermsOfServices"
              className="text-[#007AFF] hover:underline"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
